import React, { useCallback, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { Button, Tooltip, Progress } from 'antd';
import Table from '@components/Table';
import {
  bytesFormat,
  extractDateInISOFormat,
  getDateFromPartiallyInIsoFormat,
} from '@utils/formatters';

import { RiCalendarTodoFill } from 'react-icons/ri';
import { PlusCircleOutlined } from '@ant-design/icons';
import useSwr from '@hooks/useSwr';
import CustomPopover from '@components/CustomPopover';
import BlockStatusTag from '@components/BlockStatusTag';
import CustomTag from '@components/CustomTag';
import { getBlockStatusColor } from '@utils/getBlockStatusColor';

interface TagDiagnosticsProps {
  msisdn: number;
}

const TagDiagnostics = ({ msisdn }: TagDiagnosticsProps) => {
  const { t } = useTranslation([
    'inventory',
    'attributes',
    'inventory-detailed',
  ]);

  const { data } = useSwr('/service-proxy/inventory/MOT/diagnostico-simcard', {
    params: {
      msisdn,
    },
  });

  const columnFranchise = useCallback(
    (value: number, record: any) => {
      const { simcardCredit, sharedCredit } = record;

      const dataContentPopover = [
        {
          title: t('franchise.sim-card'),
          value: `+ ${bytesFormat(simcardCredit)}`,
          color: 'green',
          show: simcardCredit,
        },
        {
          title: t('franchise.contract'),
          value: `+ ${bytesFormat(sharedCredit)}`,
          color: 'blue',
          show: sharedCredit,
        },
      ];

      const sizeFranchising = bytesFormat(value);

      return (
        <div>
          {value && value !== 0 && (
            <>
              <span style={{ margin: 0, fontSize: '0.86rem' }}>
                {sizeFranchising}
              </span>
              {(sharedCredit && sharedCredit !== 0) ||
              (simcardCredit && simcardCredit !== 0) ? (
                <CustomPopover
                  title={t('table.franchising')}
                  titleValue={bytesFormat(value)}
                  titleContent={t('franchise.additional')}
                  dataContent={dataContentPopover.filter(
                    item => item.show !== 0 && item.show,
                  )}
                >
                  <Button type="link" style={{ padding: 0, marginLeft: 6 }}>
                    <PlusCircleOutlined />
                  </Button>
                </CustomPopover>
              ) : (
                <></>
              )}
            </>
          )}
        </div>
      );
    },
    [t],
  );

  const columnConsumption = useCallback(
    (value: number, record: any) => {
      const {
        consumptionTotal,
        consumptionCurrentDay,
        consumptionPercent,
        consumptionValidated,
        simcardCredit,
      } = record;

      const percent = consumptionPercent;

      const percentValidated =
        consumptionValidated && consumptionTotal
          ? (Number(consumptionValidated) * consumptionPercent) /
            (consumptionTotal + (simcardCredit ?? 0))
          : 0;

      let strokeColorDark = '#27B502';
      let strokeColor = '#2FD105';
      if (percent >= 80 && percent <= 100) {
        strokeColorDark = '#EA9801';
        strokeColor = '#ffa500';
      } else if (percent > 100) {
        strokeColorDark = '#CD022A';
        strokeColor = '#EC0231';
      }

      const sizeConsumption = bytesFormat(value);
      const sizeConsumptionCurrentDay = bytesFormat(consumptionCurrentDay);
      const sizeConsumptionValidated = bytesFormat(consumptionValidated);
      const sizeFranchising = bytesFormat(
        record.franchise + (simcardCredit || 0),
      );
      const dataContentPopover = [
        {
          title: t('consumption.validated'),
          value: sizeConsumptionValidated,
          color: strokeColor,
          show: consumptionValidated,
        },
        {
          title: t('consumption.daily'),
          value: sizeConsumptionCurrentDay,
          color: strokeColorDark,
          show: consumptionCurrentDay,
        },
      ];

      return (
        <div className="col-line-consumption">
          <p style={{ margin: 0, fontSize: '0.86rem' }}>
            {percent && percent !== 0
              ? `${sizeConsumption} / ${sizeFranchising}`
              : sizeConsumption}
          </p>
          {percent !== 0 && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Tooltip
                title={`${
                  percent && percent >= 1
                    ? Math.trunc(percent)
                    : percent?.toFixed(2)
                }%`}
              >
                <Progress
                  percent={percent}
                  showInfo={false}
                  strokeColor={strokeColorDark}
                  success={{
                    percent: percentValidated,
                    strokeColor,
                  }}
                  className="progress-bar-consumption"
                />
              </Tooltip>

              <CustomPopover
                title={t('table.consumption')}
                titleValue={sizeConsumption}
                titleContent=""
                dataContent={dataContentPopover.filter(
                  item => item.show !== 0 && item.show,
                )}
              >
                <Button
                  type="link"
                  style={{ padding: 0, margin: 0, marginLeft: 6 }}
                >
                  <PlusCircleOutlined />
                </Button>
              </CustomPopover>
            </div>
          )}
        </div>
      );
    },
    [t],
  );

  const handleSuspensionDate = useCallback((text: string, record: any) => {
    const initialDate = getDateFromPartiallyInIsoFormat(
      record.suspensionInitialDate,
    );
    const finalDate = getDateFromPartiallyInIsoFormat(
      record.suspensionFinalDate,
    );
    if (initialDate || finalDate) {
      const date = `${text ? `${text}: ` : ''}${initialDate ?? ''}${
        finalDate ? ` - ${finalDate}` : ''
      }`;
      return (
        <Tooltip title={date}>
          <RiCalendarTodoFill
            style={{
              margin: 2,
              opacity: 0.4,
              color: 'green',
              cursor: 'pointer',
              fontSize: 18,
              marginBottom: '-4px',
            }}
          />
        </Tooltip>
      );
    }
    return undefined;
  }, []);
  const columns = useMemo(
    () => [
      {
        title: t('table.client'),
        dataIndex: 'customerName',
        align: 'center' as const,
      },
      {
        title: t('table.status'),
        dataIndex: 'status',
        align: 'center' as const,
        render: (status: string, record: any) => (
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
            }}
          >
            <CustomTag color={getBlockStatusColor(status)}>
              {t(`status:${status}`).toUpperCase()}
            </CustomTag>
            {!status && handleSuspensionDate(status, record)}
          </div>
        ),
      },
      {
        title: t('table.consumption'),
        dataIndex: 'consumptionTotal',
        align: 'center' as const,
        render: (value: number, row) =>
          value ? columnConsumption(value, row) : undefined,
      },
      {
        title: t('table.franchise'),
        dataIndex: 'franchise',
        align: 'center' as const,
        render: (value: number, row) =>
          value ? columnFranchise(value, row) : undefined,
      },
      {
        title: t('attributes:automaticBlocking.title'),
        dataIndex: 'automaticBlocking',
        align: 'center' as const,
        render: (value: boolean) => (
          <CustomTag color={value ? 'green' : 'red'}>
            {t(
              `attributes:automaticBlocking.${
                value ? 'activated' : 'disabled'
              }`,
            ).toUpperCase()}
          </CustomTag>
        ),
      },
      {
        title: t('table.networkBlockStatus'),
        dataIndex: 'networkBlockStatus',
        align: 'center' as const,
        render: (value, record) => (
          <BlockStatusTag
            blockType={value}
            blockingInitialDate={record.networkBlockStartDate}
            blockingFinalDate={record.networkBlockEndDate}
          />
        ),
      },
      {
        title: t('table.networkBlockEndDate'),
        dataIndex: 'networkBlockEndDate',
        align: 'center' as const,
        render: extractDateInISOFormat,
      },
      {
        title: t('table.networkBlockStartDate'),
        dataIndex: 'networkBlockStartDate',
        align: 'center' as const,
        render: extractDateInISOFormat,
      },
      {
        title: t('table.suspensionEndDate'),
        dataIndex: 'suspensionEndDate',
        align: 'center' as const,
        render: extractDateInISOFormat,
      },
      {
        title: t('table.suspensionStartDate'),
        dataIndex: 'suspensionStartDate',
        align: 'center' as const,
        render: extractDateInISOFormat,
      },
      {
        title: t('table.cancellationDate'),
        dataIndex: 'cancellationDate',
        render: extractDateInISOFormat,
      },
    ],
    [t, handleSuspensionDate, columnConsumption, columnFranchise],
  );

  return (
    <Table
      columns={columns}
      data={data}
      customRowKey="id"
      loading={!data}
      title={t('inventory-detailed:preview-tabs.diagnostics')}
      tableKey="diagnostics"
    />
  );
};

export default TagDiagnostics;
